import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { formatCurrency } from "../utils/money";

const getInitialPrice = (service) =>
  service.priceTiers?.[0]?.price ? Number(service.priceTiers[0].price) : Number(service.basePrice) || 0;

const normalizeVat = (vat) => {
  let numVat = vat === "" ? "" : Number(vat);
  if (typeof numVat === "number") {
    numVat = Math.max(0, Math.min(10, numVat));
  }
  return numVat;
};

export const useSelectedServices = () => {
  const qtyInputRef = useRef(null);
  const selectedServiceSeqRef = useRef(0);
  const tierQtyDebounceRef = useRef({});
  const selectedServicesRef = useRef([]);

  const [tempTierQuantities, setTempTierQuantities] = useState({});
  const [visibleTiers, setVisibleTiers] = useState({});
  const [selectedServices, setSelectedServices] = useState([]);

  const [specialist, setSpecialist] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [customPricing, setCustomPricing] = useState(false);

  const [editingServiceNameId, setEditingServiceNameId] = useState(null);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [editingTierNameId, setEditingTierNameId] = useState(null);
  const [editingTierPriceId, setEditingTierPriceId] = useState(null);
  const [editingTierLicenseFeeId, setEditingTierLicenseFeeId] = useState(null);
  const [editingAdditionalFeeNameId, setEditingAdditionalFeeNameId] = useState(null);
  const [editingAdditionalFeePriceId, setEditingAdditionalFeePriceId] = useState(null);

  useEffect(() => {
    const timers = tierQtyDebounceRef.current;
    return () => {
      Object.values(timers).forEach((timer) => timer && clearTimeout(timer));
    };
  }, []);

  useEffect(() => {
    selectedServicesRef.current = selectedServices;
  }, [selectedServices]);

  const addService = (service) => {
    selectedServiceSeqRef.current += 1;
    const instanceId = `${service.id}-${Date.now()}-${selectedServiceSeqRef.current}`;
    const initialPrice = getInitialPrice(service);

    setSelectedServices((prev) => {
      return [
        ...prev,
        {
          ...service,
          id: instanceId,
          originalServiceId: service.id,
          customName: service.name,
          quantity: 1,
          customPrice: initialPrice,
          displayPrice: initialPrice,
          licenseFee: Number(service.licenseFee) || 0,
          additionalFee: Number(service.additionalFee) || 0,
          additionalFeeName: "Phí bổ sung",
          additionalFeePrice: Number(service.additionalFee) || 0,
          additionalFeeQuantity: 1,
          additionalFeeVisible: false,
          customTierNames:
            service.priceTiers?.reduce((acc, tier, idx) => {
              acc[idx] = tier.rangeName || `${tier.minQty} - ${tier.maxQty || "vô hạn"} dịch vụ`;
              return acc;
            }, {}) || {},
          customTierPrices:
            service.priceTiers?.reduce((acc, tier, idx) => {
              acc[idx] = Number(tier.price) || 0;
              return acc;
            }, {}) || {},
          customTierLicenseFees:
            service.priceTiers?.reduce((acc, tier, idx) => {
              acc[idx] = Number(tier.licenseFee || tier.certificateFee || tier.issueFee) || 0;
              return acc;
            }, {}) || {},
          tierQuantities:
            service.priceTiers?.map((_, idx) => ({ tierIndex: idx, quantity: 1 })) || [],
          tierVats:
            service.priceTiers && service.priceTiers.length > 0
              ? service.priceTiers.reduce((acc, _, idx) => {
                  acc[idx] = 10;
                  return acc;
                }, {})
              : { 0: 10 },
          additionalFeeVat: 10,
        },
      ];
    });
  };

  const removeService = (id) => {
    setSelectedServices((prev) => prev.filter((service) => service.id !== id));
    setVisibleTiers((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const showNextTier = (serviceId) => {
    setVisibleTiers((prev) => {
      const current = prev[serviceId] || [0];
      const service = selectedServices.find((item) => item.id === serviceId);
      if (!service?.priceTiers) return prev;

      for (let index = 0; index < service.priceTiers.length; index += 1) {
        if (!current.includes(index)) {
          return { ...prev, [serviceId]: [...current, index].sort((a, b) => a - b) };
        }
      }
      return prev;
    });
  };

  const removeTier = (serviceId, tierIndex) => {
    if (tierIndex === 0) return;

    setVisibleTiers((prev) => {
      const current = prev[serviceId] || [0];
      return { ...prev, [serviceId]: current.filter((index) => index !== tierIndex) };
    });

    setSelectedServices((prev) =>
      prev.map((service) =>
        service.id === serviceId
          ? {
              ...service,
              tierQuantities: (service.tierQuantities || []).filter((tq) => tq.tierIndex !== tierIndex),
            }
          : service,
      ),
    );

    const debounceKey = `${serviceId}-${tierIndex}`;
    setTempTierQuantities((prev) => {
      const next = { ...prev };
      delete next[debounceKey];
      return next;
    });
  };

  const updateSelectedService = (serviceId, updater) => {
    setSelectedServices((prev) =>
      prev.map((service) => (service.id === serviceId ? updater(service) : service)),
    );
  };

  const updateCustomServiceName = (serviceId, value) => {
    updateSelectedService(serviceId, (service) => ({ ...service, customName: value }));
  };

  const updateCustomPrice = (serviceId, price) => {
    updateSelectedService(serviceId, (service) => ({
      ...service,
      customPrice: price === "" ? "" : parseInt(price, 10) || 0,
      displayPrice: price === "" ? "" : parseInt(price, 10) || 0,
    }));
  };

  const updateLicenseFee = (serviceId, price) => {
    updateSelectedService(serviceId, (service) => ({
      ...service,
      licenseFee: price === "" ? "" : Number(price),
    }));
  };

  const updateTierVat = (serviceId, tierIndex, vat) => {
    updateSelectedService(serviceId, (service) => ({
      ...service,
      tierVats: { ...(service.tierVats || {}), [tierIndex]: normalizeVat(vat) },
    }));
  };

  const updateAdditionalFeeVat = (serviceId, vat) => {
    updateSelectedService(serviceId, (service) => ({
      ...service,
      additionalFeeVat: normalizeVat(vat),
    }));
  };

  const updateQuantity = (id, quantity) => {
    updateSelectedService(id, (service) => ({
      ...service,
      quantity: quantity === "" ? "" : Number(quantity),
    }));
  };

  const normalizeQuantity = (id) => {
    updateSelectedService(id, (service) => {
      const quantity = Number(service.quantity);
      return { ...service, quantity: !quantity || quantity <= 0 ? 1 : quantity };
    });
  };

  const updateCustomTierName = (serviceId, tierIndex, value) => {
    updateSelectedService(serviceId, (service) => ({
      ...service,
      customTierNames: { ...service.customTierNames, [tierIndex]: value },
    }));
  };

  const updateCustomTierPrice = (serviceId, tierIndex, value) => {
    updateSelectedService(serviceId, (service) => ({
      ...service,
      customTierPrices: {
        ...service.customTierPrices,
        [tierIndex]: value === "" ? "" : Number(value),
      },
    }));
  };

  const updateCustomTierLicenseFee = (serviceId, tierIndex, value) => {
    updateSelectedService(serviceId, (service) => ({
      ...service,
      customTierLicenseFees: {
        ...service.customTierLicenseFees,
        [tierIndex]: value === "" ? "" : Number(value),
      },
    }));
  };

  const updateAdditionalFeeName = (id, name) => {
    updateSelectedService(id, (service) => ({ ...service, additionalFeeName: name }));
  };

  const updateAdditionalFeePrice = (id, price) => {
    updateSelectedService(id, (service) => ({
      ...service,
      additionalFeePrice: price === "" ? "" : Number(price),
    }));
  };

  const updateAdditionalFeeQuantity = (id, qty) => {
    updateSelectedService(id, (service) => ({
      ...service,
      additionalFeeQuantity: qty === "" ? "" : Number(qty),
    }));
  };

  const showAdditionalFee = (id) => {
    updateSelectedService(id, (service) => ({ ...service, additionalFeeVisible: true }));
  };

  const hideAdditionalFee = (id) => {
    updateSelectedService(id, (service) => ({ ...service, additionalFeeVisible: false }));
  };

  const handleTierQuantityChange = (serviceId, tierIndex, value) => {
    const debounceKey = `${serviceId}-${tierIndex}`;

    setTempTierQuantities((prev) => ({ ...prev, [debounceKey]: value }));

    if (tierQtyDebounceRef.current[debounceKey]) {
      clearTimeout(tierQtyDebounceRef.current[debounceKey]);
    }

    tierQtyDebounceRef.current[debounceKey] = setTimeout(() => {
      const service = selectedServicesRef.current.find((item) => item.id === serviceId);
      if (!service) return;

      const tier = service.priceTiers?.[tierIndex];
      const numValue = value === "" ? NaN : Number(value);

      if (!tier) {
        if (Number.isNaN(numValue) || numValue < 1) {
          toast.error("Số lượng tối thiểu là 1");
          const oldQty = service.tierQuantities?.find((tq) => tq.tierIndex === tierIndex)?.quantity || 1;
          setTempTierQuantities((prev) => ({ ...prev, [debounceKey]: oldQty }));
        } else {
          updateTierQuantity(serviceId, tierIndex, numValue, debounceKey);
        }
        return;
      }

      const minQty = Number(tier.minQty) || 1;
      const maxQty = tier.maxQty ? Number(tier.maxQty) : Infinity;
      const maxAllowedInRow = maxQty !== Infinity ? maxQty - minQty + 1 : Infinity;

      if (Number.isNaN(numValue) || numValue < 1 || numValue > maxAllowedInRow) {
        const rangeMsg = maxAllowedInRow === Infinity ? "từ 1 trở lên" : `từ 1 đến ${maxAllowedInRow}`;
        toast.error(`Số lượng cho bậc "${tier.rangeName || "này"}" phải ${rangeMsg}`);
        const oldQty = service.tierQuantities?.find((tq) => tq.tierIndex === tierIndex)?.quantity || 1;
        setTempTierQuantities((prev) => ({ ...prev, [debounceKey]: oldQty }));
        return;
      }

      updateTierQuantity(serviceId, tierIndex, numValue, debounceKey);
    }, 800);
  };

  const updateTierQuantity = (serviceId, tierIndex, validQty, debounceKey) => {
    updateSelectedService(serviceId, (service) => {
      const tqs = [...(service.tierQuantities || [])];
      const index = tqs.findIndex((item) => item.tierIndex === tierIndex);
      if (index >= 0) tqs[index] = { tierIndex, quantity: validQty };
      else tqs.push({ tierIndex, quantity: validQty });
      return { ...service, tierQuantities: tqs };
    });

    setTempTierQuantities((prev) => {
      const next = { ...prev };
      delete next[debounceKey];
      return next;
    });
  };

  const getMainSubtotal = (service) => {
    const quantity = Number(service.quantity) || 1;
    const price = Number(service.displayPrice) || 0;
    const licenseFee = Number(service.licenseFee) || 0;
    return (price + licenseFee) * quantity;
  };

  const calculateTotal = () =>
    selectedServices.reduce((total, service) => {
      const currentVisibleTiers = visibleTiers[service.id] || [0];
      let serviceTotal = 0;

      currentVisibleTiers.forEach((tierIndex) => {
        if (tierIndex === 0) {
          serviceTotal += getMainSubtotal(service);
          return;
        }

        const tier = service.priceTiers?.[tierIndex];
        if (!tier) return;

        const tierPrice =
          service.customTierPrices?.[tierIndex] !== undefined
            ? Number(service.customTierPrices[tierIndex]) || 0
            : Number(tier.price) || 0;
        const tierLicenseFee =
          service.customTierLicenseFees?.[tierIndex] !== undefined
            ? Number(service.customTierLicenseFees[tierIndex]) || 0
            : Number(tier.licenseFee || tier.certificateFee || tier.issueFee) || 0;
        const tierQty = service.tierQuantities?.find((tq) => tq.tierIndex === tierIndex)?.quantity || 1;
        serviceTotal += (tierPrice + tierLicenseFee) * tierQty;
      });

      if (service.additionalFeeVisible) {
        const addFeePrice =
          service.additionalFeePrice !== undefined
            ? Number(service.additionalFeePrice)
            : Number(service.additionalFee) || 0;
        const addFeeQty =
          service.additionalFeeQuantity !== undefined ? Number(service.additionalFeeQuantity) : 1;
        serviceTotal += addFeePrice * addFeeQty;
      }

      return total + serviceTotal;
    }, 0);

  const calculateTotalVat = () =>
    selectedServices.reduce((totalVat, service) => {
      const currentVisibleTiers = visibleTiers[service.id] || [0];
      let serviceVatSum = 0;

      currentVisibleTiers.forEach((tierIndex) => {
        let subtotal = 0;
        if (tierIndex === 0) {
          subtotal = getMainSubtotal(service);
        } else {
          const tier = service.priceTiers?.[tierIndex];
          if (!tier) return;
          const tierPrice =
            service.customTierPrices?.[tierIndex] !== undefined
              ? Number(service.customTierPrices[tierIndex]) || 0
              : Number(tier.price) || 0;
          const tierLicenseFee =
            service.customTierLicenseFees?.[tierIndex] !== undefined
              ? Number(service.customTierLicenseFees[tierIndex]) || 0
              : Number(tier.licenseFee || tier.certificateFee || tier.issueFee) || 0;
          const tierQty = service.tierQuantities?.find((tq) => tq.tierIndex === tierIndex)?.quantity || 1;
          subtotal = (tierPrice + tierLicenseFee) * tierQty;
        }

        const vatPercent = Number(service.tierVats?.[tierIndex]) || 0;
        serviceVatSum += Math.round((subtotal * vatPercent) / 100);
      });

      if (service.additionalFeeVisible) {
        const addFeePrice =
          service.additionalFeePrice !== undefined
            ? Number(service.additionalFeePrice)
            : Number(service.additionalFee) || 0;
        const addFeeQty =
          service.additionalFeeQuantity !== undefined ? Number(service.additionalFeeQuantity) : 1;
        const addFeeSubtotal = addFeePrice * addFeeQty;
        serviceVatSum += Math.round((addFeeSubtotal * (Number(service.additionalFeeVat) || 0)) / 100);
      }

      return totalVat + serviceVatSum;
    }, 0);

  const calculateFinalTotal = () => calculateTotal() + calculateTotalVat();

  return {
    specialist,
    setSpecialist,
    email,
    setEmail,
    phone,
    setPhone,
    customPricing,
    setCustomPricing,

    selectedServices,
    visibleTiers,
    tempTierQuantities,

    addService,
    removeService,
    showNextTier,
    removeTier,
    updateCustomServiceName,
    updateCustomPrice,
    updateLicenseFee,
    updateQuantity,
    normalizeQuantity,
    updateCustomTierName,
    updateCustomTierPrice,
    updateCustomTierLicenseFee,
    updateAdditionalFeeName,
    updateAdditionalFeePrice,
    updateAdditionalFeeQuantity,
    showAdditionalFee,
    hideAdditionalFee,
    handleTierQuantityChange,
    updateTierVat,
    updateAdditionalFeeVat,

    editingServiceNameId,
    setEditingServiceNameId,
    editingPriceId,
    setEditingPriceId,
    editingTierNameId,
    setEditingTierNameId,
    editingTierPriceId,
    setEditingTierPriceId,
    editingTierLicenseFeeId,
    setEditingTierLicenseFeeId,
    editingAdditionalFeeNameId,
    setEditingAdditionalFeeNameId,
    editingAdditionalFeePriceId,
    setEditingAdditionalFeePriceId,

    calculateTotal,
    calculateTotalVat,
    calculateFinalTotal,
    formatCurrency,
    qtyInputRef,
  };
};
